import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Search, Leaf, ChevronDown, ChevronUp, Camera, Loader2, Download, Upload, Plus, X } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import ImportExcelModal from '../../components/shared/ImportExcelModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIAS = ['Todas', 'Proteina', 'Vegetal', 'Fruta', 'Carbohidrato', 'Grasa', 'Lacteo', 'Condimento', 'Bebida'];
const CATEGORIAS_FORM = ['Proteina', 'Vegetal', 'Fruta', 'Carbohidrato', 'Grasa', 'Lacteo', 'Condimento', 'Bebida'];

const CAT_COLORS = {
  'Proteina': { bg: 'bg-rose-100', text: 'text-rose-700' },
  'Vegetal': { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  'Fruta': { bg: 'bg-amber-100', text: 'text-amber-700' },
  'Carbohidrato': { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  'Grasa': { bg: 'bg-orange-100', text: 'text-orange-700' },
  'Lacteo': { bg: 'bg-blue-100', text: 'text-blue-700' },
  'Condimento': { bg: 'bg-purple-100', text: 'text-purple-700' },
  'Bebida': { bg: 'bg-cyan-100', text: 'text-cyan-700' },
};

const ImageUploader = ({ itemId, currentUrl, collection, onUpdated }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/admin/diet-image/${collection}/${itemId}`, formData);
      onUpdated(res.data.image_url);
    } catch (error) { console.error('Upload failed:', error); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="relative group">
      <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 flex-shrink-0">
        {currentUrl ? (
          <img src={currentUrl.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${currentUrl}` : currentUrl}
            alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300"><Leaf size={16} /></div>
        )}
      </div>
      <button onClick={(ev) => { ev.stopPropagation(); fileRef.current?.click(); }}
        className="absolute inset-0 bg-black/40 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
        data-testid={`upload-img-${itemId}`}>
        {uploading ? <Loader2 size={14} className="text-white animate-spin" /> : <Camera size={14} className="text-white" />}
      </button>
      <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
    </div>
  );
};

const EMPTY_ING = { nombre: '', descripcion: '', categoria: 'Vegetal', unidad: 'g', gramos_por_unidad: 100, calorias: 0, grasa: 0, proteinas: 0, carbohidratos: 0, beneficios: '' };

const CreateIngredienteModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ ...EMPTY_ING });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    if (!form.nombre.trim()) { toast.error('El nombre es obligatorio'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/ingredientes`, form);
      toast.success('Ingrediente creado exitosamente');
      onCreated(res.data);
      onClose();
    } catch (error) { toast.error('Error al crear ingrediente'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="create-ingrediente-modal">
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-black text-[#1A2E10]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Nuevo Ingrediente</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center"><X size={18} className="text-gray-500" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Nombre *</label>
            <Input value={form.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Nombre del ingrediente" className="mt-1" data-testid="ing-nombre" />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Descripcion</label>
            <Input value={form.descripcion} onChange={e => set('descripcion', e.target.value)} placeholder="Descripcion breve" className="mt-1" data-testid="ing-descripcion" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Categoria</label>
              <select value={form.categoria} onChange={e => set('categoria', e.target.value)}
                className="mt-1 w-full h-10 px-3 rounded-xl border border-gray-200 text-sm focus:border-[#467322] outline-none" data-testid="ing-categoria">
                {CATEGORIAS_FORM.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Unidad</label>
              <select value={form.unidad} onChange={e => set('unidad', e.target.value)}
                className="mt-1 w-full h-10 px-3 rounded-xl border border-gray-200 text-sm focus:border-[#467322] outline-none">
                <option value="g">g</option><option value="ml">ml</option><option value="oz">oz</option><option value="unidad">unidad</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Gramos/Unidad</label>
              <Input type="number" value={form.gramos_por_unidad} onChange={e => set('gramos_por_unidad', Number(e.target.value))} className="mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Calorias</label>
              <Input type="number" value={form.calorias} onChange={e => set('calorias', Number(e.target.value))} className="mt-1" data-testid="ing-calorias" />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Grasa</label>
              <Input type="number" value={form.grasa} onChange={e => set('grasa', Number(e.target.value))} className="mt-1" />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Proteinas</label>
              <Input type="number" value={form.proteinas} onChange={e => set('proteinas', Number(e.target.value))} className="mt-1" />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Carbos</label>
              <Input type="number" value={form.carbohidratos} onChange={e => set('carbohidratos', Number(e.target.value))} className="mt-1" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Beneficios</label>
            <Input value={form.beneficios} onChange={e => set('beneficios', e.target.value)} placeholder="Beneficios del ingrediente" className="mt-1" data-testid="ing-beneficios" />
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold text-sm hover:bg-gray-50">Cancelar</button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-[#467322] text-white font-bold text-sm hover:bg-[#3A601C] transition-colors disabled:opacity-50"
            data-testid="ing-save-btn">
            {saving ? <Loader2 size={14} className="inline animate-spin mr-1" /> : null} Guardar
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminIngredientesPage = () => {
  const [ingredientes, setIngredientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('Todas');
  const [expandedId, setExpandedId] = useState(null);
  const [sortBy, setSortBy] = useState('nombre');
  const [showImport, setShowImport] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const fetchIngredientes = useCallback(() => {
    axios.get(`${API}/admin/ingredientes`).then(res => setIngredientes(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchIngredientes(); }, [fetchIngredientes]);

  const updateImage = (id, url) => {
    setIngredientes(prev => prev.map(i => i.id === id ? { ...i, image_url: url } : i));
  };

  const exportData = () => {
    const blob = new Blob([JSON.stringify(ingredientes, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ingredientes_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = ingredientes.filter(i => {
    const matchSearch = !search || i.nombre?.toLowerCase().includes(search.toLowerCase());
    const matchCat = filterCat === 'Todas' || i.categoria === filterCat;
    return matchSearch && matchCat;
  }).sort((a, b) => {
    if (sortBy === 'nombre') return (a.nombre || '').localeCompare(b.nombre || '');
    if (sortBy === 'calorias') return (b.calorias || 0) - (a.calorias || 0);
    if (sortBy === 'proteinas') return (b.proteinas || 0) - (a.proteinas || 0);
    return 0;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#467322]" size={32} /></div>;

  return (
    <div data-testid="admin-ingredientes-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Ingredientes</h1>
          <p className="text-gray-500 text-sm mt-1">{filtered.length} de {ingredientes.length} ingredientes</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowImport(true)}
            className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 hover:border-[#467322] hover:text-[#467322] transition-all shadow-sm"
            data-testid="import-ingredientes-btn">
            <Upload size={15} /><span className="hidden sm:inline">Importar</span>
          </button>
          <button onClick={exportData}
            className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 hover:border-[#467322] hover:text-[#467322] transition-all shadow-sm"
            data-testid="export-ingredientes-btn">
            <Download size={15} /><span className="hidden sm:inline">Exportar</span>
          </button>
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#467322] text-white text-sm font-bold hover:bg-[#3A601C] transition-colors shadow-sm shadow-green-900/20"
            data-testid="new-ingrediente-btn">
            <Plus size={15} /><span className="hidden sm:inline">Nuevo</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-5">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Buscar ingrediente..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9 bg-gray-50 border-transparent focus:bg-white focus:border-[#467322] rounded-xl h-10" data-testid="search-ingredientes" />
          </div>
          <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
            className="h-10 px-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:border-[#467322] outline-none" data-testid="filter-categoria">
            {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
            className="h-10 px-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:border-[#467322] outline-none" data-testid="sort-ingredientes">
            <option value="nombre">Nombre A-Z</option>
            <option value="calorias">Mas Calorias</option>
            <option value="proteinas">Mas Proteinas</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left text-[11px] font-bold uppercase tracking-wider text-gray-400 px-4 py-3 w-16"></th>
                <th className="text-left text-[11px] font-bold uppercase tracking-wider text-gray-400 px-4 py-3">Nombre</th>
                <th className="text-left text-[11px] font-bold uppercase tracking-wider text-gray-400 px-4 py-3 hidden md:table-cell">Categoria</th>
                <th className="text-center text-[11px] font-bold uppercase tracking-wider text-gray-400 px-4 py-3">Unidad</th>
                <th className="text-center text-[11px] font-bold uppercase tracking-wider text-gray-400 px-4 py-3">Calorias</th>
                <th className="text-center text-[11px] font-bold uppercase tracking-wider text-gray-400 px-4 py-3 hidden lg:table-cell">Grasa</th>
                <th className="text-center text-[11px] font-bold uppercase tracking-wider text-gray-400 px-4 py-3 hidden lg:table-cell">Proteinas</th>
                <th className="text-center text-[11px] font-bold uppercase tracking-wider text-gray-400 px-4 py-3 hidden lg:table-cell">Carbs</th>
                <th className="text-center text-[11px] font-bold uppercase tracking-wider text-gray-400 px-2 py-3 w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(ing => {
                const isExpanded = expandedId === ing.id;
                const catStyle = CAT_COLORS[ing.categoria] || { bg: 'bg-gray-100', text: 'text-gray-600' };
                return (
                  <React.Fragment key={ing.id}>
                    <tr className="hover:bg-gray-50/50 transition-colors cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : ing.id)} data-testid={`ing-row-${ing.id}`}>
                      <td className="px-4 py-2">
                        <ImageUploader itemId={ing.id} currentUrl={ing.image_url} collection="ingredientes" onUpdated={url => updateImage(ing.id, url)} />
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-bold text-gray-900 text-sm" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{ing.nombre}</p>
                        <p className="text-xs text-gray-400 md:hidden">{ing.categoria}</p>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-md ${catStyle.bg} ${catStyle.text}`}>{ing.categoria}</span>
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-gray-600">{ing.gramos_por_unidad}{ing.unidad}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-sm font-bold text-gray-800">{ing.calorias}</span>
                        <span className="text-[10px] text-gray-400 ml-0.5">kcal</span>
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-gray-600 hidden lg:table-cell">{ing.grasa}g</td>
                      <td className="px-4 py-3 text-center text-sm text-gray-600 hidden lg:table-cell">{ing.proteinas}g</td>
                      <td className="px-4 py-3 text-center text-sm text-gray-600 hidden lg:table-cell">{ing.carbohidratos}g</td>
                      <td className="px-2 py-3 text-center">
                        {isExpanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={9} className="px-4 py-4 bg-gray-50/50 animate-fade-in">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="bg-white rounded-xl p-3 border border-gray-100">
                              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Descripcion</p>
                              <p className="text-sm text-gray-700">{ing.descripcion}</p>
                            </div>
                            <div className="bg-white rounded-xl p-3 border border-gray-100">
                              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Beneficios</p>
                              <p className="text-sm text-gray-700">{ing.beneficios}</p>
                            </div>
                            <div className="bg-white rounded-xl p-3 border border-gray-100">
                              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Macros por unidad</p>
                              <div className="flex gap-3 text-sm">
                                <span className="text-amber-600 font-bold">G:{ing.grasa}g</span>
                                <span className="text-rose-600 font-bold">P:{ing.proteinas}g</span>
                                <span className="text-blue-600 font-bold">C:{ing.carbohidratos}g</span>
                              </div>
                            </div>
                            <div className="bg-white rounded-xl p-3 border border-gray-100">
                              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Porcion</p>
                              <p className="text-sm text-gray-700 font-semibold">{ing.gramos_por_unidad} {ing.unidad}</p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <Leaf size={40} className="mx-auto mb-3 opacity-30" />
            <p className="font-semibold">No se encontraron ingredientes</p>
          </div>
        )}
      </div>

      {showImport && <ImportExcelModal collection="ingredientes" onClose={() => setShowImport(false)} onImported={fetchIngredientes} />}
      {showCreate && <CreateIngredienteModal onClose={() => setShowCreate(false)} onCreated={(i) => setIngredientes(prev => [i, ...prev])} />}
    </div>
  );
};

export default AdminIngredientesPage;
